/**
 * Configuración centralizada de la aplicación
 *
 * IMPORTANTE: Este archivo contiene la configuración específica de PyLC.
 * Para usar este proyecto con otra empresa, solo necesitas:
 * 1. Cambiar los valores en este archivo
 * 2. Reemplazar los assets en la carpeta correspondiente
 * 3. El resto del proyecto se adaptará automáticamente
 *
 * Este archivo contiene:
 * - Información de la empresa (PyLC específico)
 * - Colores y temas (PyLC específico) - FUENTE DE VERDAD para SCSS
 * - Logos y assets (PyLC específico)
 * - URLs y redirecciones (PyLC específico)
 * - Configuraciones generales (reutilizable)
 *
 * NOTA: Los colores definidos aquí se sincronizan automáticamente con
 * las variables SCSS a través de configHelpers.ts
 */

import JotformEmbed from '@/components/jotform/JotformEmbed.vue';
import { ProjectColorsConfig } from '@/types/config/project';

// ===========================================
// INFORMACIÓN DE LA EMPRESA
// ===========================================
export const companyConfig = {
    name: 'PyLC',
    fullName: 'Ejecutivo',
    description: 'Plataforma Ejecutiva de Seguros',
    tagline: 'Tu socio en seguros',
    version: '2.6.1', // Se sincroniza con package.json
    typeUserOne: "Enlace",
    typeUserOnePlural: "Enlaces",
    typeUserTwo: "Analista",
    typeUserTwoPlural: "Analistas",
    tennantCompany: '',

    // URLs de redirección
    urls: {
        website: 'https://pylc.com',
        support: 'https://support.pylc.com',
        documentation: 'https://docs.pylc.com',
        terms: '/aviso-privacidad',
        privacy: '/aviso-privacidad'
    },

    // Información de contacto
    contact: {
        email: 'info@pylc.com',
        phone: '+52 55 1234 5678',
        address: 'Ciudad de México, México'
    }
};

// ===========================================
// LOGOS Y ASSETS
// ===========================================
export const assetsConfig = {
    logos: {
        main: '/assets/branding/logo_pylc.svg',
        main_dark: '/assets/branding/logo_pylc_w.svg',
        icon: '/assets/branding/icon_pylc_c.svg',
        icon_dark: '/assets/branding/icon_pylc_w.svg',
        favicon: '/assets/branding/favicon_package_v5/favicon.svg',

        // Logos adicionales para diferentes contextos
        theme: '/assets/branding/favicon_package_v5/favicon.svg', // Si existe
        dark_theme: '/assets/branding/favicon_package_v5/favicon.svg', // Si existe
        theme_rtl: '/assets/branding/logo-light-rtl.svg',
        dark_theme_rtl: '/assets/branding/logo-dark-rtl.svg',
        small: '/assets/branding/favicon_package_v5/favicon.svg',

        // Iconos de folders
        icon_folder: '/assets/branding/icon_folder_pylc.svg'
    },

    // Favicons y PWA
    favicons: {
        appleTouchIcon: '/assets/branding/favicon_package_v5/apple-touch-icon.png',
        favicon32: '/assets/branding/favicon_package_v5/favicon-32x32.png',
        favicon16: '/assets/branding/favicon_package_v5/favicon-16x16.png',
        manifest: '/assets/branding/favicon_package_v5/site.webmanifest'
    },

    // Imágenes de fondo y decorativas
    images: {
        loginBackground: '', // Si existe
        dashboardBanner: '', // Si existe
        // Imagen de seguro por defecto (si no hay image_insurance en el item)
        defaultInsuranceImageLight: '/assets/branding/multi_company_arrows_clearmode.svg', // Si existe
        defaultInsuranceImageDark: '/assets/branding/multi_company_arrows_darkmode.svg' // Si existe
    },
    animations: {
        jotform: ''
    }
};

// ===========================================
// CONFIGURACIÓN DE TIPOGRAFÍA
// ===========================================
export const typographyConfig = {
    // Fuente principal de la aplicación
    fontFamily: 'Plus Jakarta Sans', // Nombre de la fuente
    // URL de Google Fonts (opcional, si la fuente está en Google Fonts)
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap',
    // Fuentes de respaldo (fallback)
    fallbackFonts: ['system-ui', 'Avenir', 'Helvetica', 'Arial', 'sans-serif'],
    // Si la fuente es de Google Fonts
    isGoogleFont: true
};

// ===========================================
// COLORES
// ===========================================

export const colorsConfig: ProjectColorsConfig = {
    light: {
        primary: '#511a6b', // Manteniendo el morado como color principal
        secondary: '#777777', // Gris como color secundario
        info: '#17a2b8', // Azul turquesa más estándar para información
        success: '#28a745', // Verde estándar para éxito
        warning: '#f8c076', // Amarillo estándar para advertencias
        error: '#dc3545', // Rojo estándar para errores
        errorSecondary: '#e57373', // Rojo más suave para errores secundarios
        indigo: '#6610f2', // Un tono más vibrante para el índigo
        lightprimary: '#f8f0fa', // Fondo más claro para "primary"
        lightinfo: '#e3f8fc', // Azul claro para "info"
        lightsecondary: '#f6f6f6', // Gris claro para fondo secundario
        lightsuccess: '#e6f9ef', // Verde claro para éxito
        lighterror: '#fde2e2', // Rojo claro para error
        lightwarning: '#fff7e6', // Amarillo claro para advertencias
        lightindigo: '#efe5fd', // Índigo claro para fondos
        textPrimary: '#212529', // Texto principal en negro intenso
        textSecondary: '#495057', // Texto secundario en gris oscuro
        borderColor: '#ced4da', // Gris claro para bordes
        inputBorder: '#dee2e6', // Gris más claro para bordes de inputs
        containerBg: '#fafafa', // Fondo de contenedores en blanco
        background: '#f4f2f8', // Fondo principal — rosita/morado tenue del rediseño (base.css: body #F4F2F8). Antes #f8f9fa (gris)
        hoverColor: '#e9ecef', // Color para hover en elementos
        surface: '#ffffff', // Superficies (tarjetas, lateral) en blanco puro, como el rediseño. Antes #fafafa
        grey100: '#adb5bd', // Gris claro para texto terciario
        grey200: '#6c757d', // Gris oscuro para detalles secundarios
        darkbg: '#343a40', // Fondo oscuro en ciertos elementos
        bglight: '#f1f3f5', // Fondo claro para contrastes
        bgdark: '#e9ecef', // Fondo oscuro para sombras
        blueBin: '#0056b3', // Azul más oscuro para contraste en bin
        greenProject: '#008f39', // Verde más estándar para "Pylc"
        greenNewProject: '#00732c', // Verde más oscuro para nuevo
        greenRenovationProject: '#a2f19e', // Verde pastel para renovación
        orangeProject: '#ff8c42', // Naranja brillante
        yellowProject: '#fadb14', // Amarillo más vibrante
        pinkSecondaryProject: '#e91e63', // Rosa vibrante
        purpleComercialProject: '#6200ea', // Morado comercial vibrante

        // ── Tokens del rediseño (prefijo ds) ────────────────────────────────
        // Valores literales del prototipo `vue-tablero/src/styles/tokens.css`.
        // Viven aquí, en la config de PyLC, y no en el core: son identidad de
        // esta marca. Otra marca que no los declare recibe una escala derivada
        // de su propia paleta (ver src/theme/dsTokens.ts).
        // Neutros teñidos de morado: misma escala de luminosidad que los grises
        // del tema, rotada de matiz, así que conservan los contrastes.
        dsInk: '#211833',
        dsText: '#4a4258',
        dsTextMuted: '#7b7391',
        dsTextSubtle: '#9c94af',
        dsTextFaint: '#b6b0c4',
        dsLineStrong: '#cfc7de',
        dsLine: '#ded8ea',
        dsBorder: '#e7e3ef',
        dsDivider: '#efecf5',
        dsBg: '#f4f2f8',
        dsRowUnread: '#f4f0f9',
        dsRowHover: '#f7f6fa',
        dsPurple700: '#3c2b6b',
        dsPurple50: '#f4f0f9',
        // Teal One360: acento de datos y estados positivos.
        dsTeal400: '#15c2ae',
        dsTeal600: '#0f9e8f',
        dsTeal700: '#0b7d71',
        dsTeal800: '#08635a',
        dsTeal50: '#e3f5f2',
        // El prototipo separa el color del texto del color del punto del
        // semáforo; el `warning` del tema solo cubre el caso de fondo.
        dsDanger: '#c42f53',
        dsDangerDot: '#ff5d83',
        dsDangerBg: '#ffe9ee',
        dsDangerOnDark: '#ff9db4', // rosa legible sobre imagen oscura
        dsWarn: '#a9670a',
        dsWarnDot: '#ffa601',
        dsWarnBg: '#fff4e0',

        // Cimiento del portal del agente: 7 tokens medidos en el prototipo
        // desempacado (usos: 34/27/18/12/11/11/9). Ver PLAN.md R3.
        dsSurfaceSubtle: '#fbfafd', // fondo de fila alterna
        dsPurple100: '#f1edf7', // fondo de chip morado
        dsWarnBorder: '#ffe2b0', // borde de aviso ámbar
        dsDangerBgSoft: '#fff1f4', // fondo de alerta claro
        dsLinePurple: '#e0d8ee', // línea morada sobre fondo teñido
        dsLinePurpleSoft: '#e7e1f3', // borde de la burbuja de chat del agente (entrega-vue folios.js)
        dsPurple500: '#755dd9', // morado brillante de acento
        dsSurfaceHover: '#f9f8fc', // hover muy tenue

        // Backlog #8289 · task 1.28 (#8554) — tokens que faltaban para Pólizas
        // pixel-exact. Medidos de entrega-vue (PolizasView.vue / polizas.js).
        // Resuelven la inconsistencia de dsWarnBg (que se estaba usando como dos
        // colores) separando el fondo tenue del banner en su propio token.
        dsWarnBgSoft: '#fffbf3', // banner ámbar tenue "falta dato" (borde dsWarnBorder). NO es dsWarnBg (#fff4e0 = píldora/fila)
        dsOkBgSoft: '#f7fcfb', // fondo tenue de cuadre OK (teal); par de dsWarnBgSoft
        dsOkBorder: '#cbede7', // borde teal del bloque de cuadre OK
        dsViewerBar: '#1e3a8a', // banda azul de la barra del visor PDF

        // Versión final (backlog #8289, task 1.1 / #8290): fondo del botón
        // secundario, medido del rediseño final. 36 usos, sin borde. §4 y §7.1.
        dsPurple08: 'rgba(81, 26, 107, 0.08)',

        // Versión final (backlog #8289, task 1.7 / #8296): control deshabilitado,
        // medido del rediseño final (§7.1).
        dsDisabledBg: '#e7ecf0',
        dsDisabledText: '#9aa4ad',
        // Superficie de tarjeta ds (task 1.21): blanco en claro.
        dsSurface: '#ffffff',

        // Estados §8 (extensión de D-colores-globales a botones/estados,
        // 2026-09-08): fondo del BLOQUE de estado, medido del rediseño donde
        // difiere del fondo de la píldora. "En PyLC" y "Cerrado" no necesitan
        // token nuevo: su bloque ya coincide con dsPurple50/dsRowHover.
        dsWarnBlockBg: '#fff9ec', // bloque "Te toca a ti" (píldora usa dsWarnBg #fff4e0)
        dsTealBlockBg: '#eaf8f6', // bloque "Entregado" (píldora usa dsTeal50 #e3f5f2)
        dsTealBlockBorder: '#b8e8e1', // borde del bloque "Entregado"
        dsDangerBlockBorder: '#fbd5de' // borde del bloque "Fuera de tiempo"
    },
    dark: {
        primary: '#e3b5f9', // Morado claro del rediseño en oscuro (= invert(0.92) hue-rotate(180) de #511a6b ≈ #e5b7fb). Antes #9e5fbf (a ojo, muy saturado): botón "Nueva cotización" y texto de menú activo salían morado oscuro en vez del lila claro del rediseño.
        secondary: '#afafaf',
        info: '#17a2b8', // Azul turquesa más estándar para información
        success: '#28a745', // Verde estándar para éxito
        warning: '#F8B15D', // Amarillo estándar para advertencias
        error: '#dc3545', // Rojo estándar para errores
        errorSecondary: '#e57373', // Rojo más suave para errores secundarios
        indigo: '#8763DA',
        lightprimary: '#f4e9fa',
        lightinfo: '#1a3247',
        lightsecondary: '#2f3f4c',
        lightsuccess: '#102d35',
        lighterror: '#72483d',
        lightwarning: '#282b32',
        textPrimary: '#EAEFF4',
        textSecondary: '#fff',
        lightindigo: '#1d233e',
        borderColor: '#2c2933',
        inputBorder: '#465670',
        containerBg: '#141414',
        background: '#25222a', // Fondo dark del <main> = resultado del filtro invert(0.92) hue-rotate(180) del rediseño sobre su contenedor de contenido (~#EFEDF5). Medido contra 5174: #25222a. Antes #201e23 (filtro de #F4F2F8, salía ~5u más oscuro)
        hoverColor: '#1c1b1e',
        surface: '#141414', // Superficie dark = dsSurface del rediseño: sidebar + todas las cards/KPIs. Antes #1e1b26 (surface base de Vuetify, salía más claro que el rediseño)
        grey100: '#7C8FAC',
        grey200: '#EAEFF4',
        darkbg: '#111936',
        bglight: '#16293e',
        bgdark: '#16293e',
        blueBin: '#0038ff',
        greenProject: '#0bc707',
        greenNewProject: '#078f04',
        greenRenovationProject: '#c1ff72',
        orangeProject: '#ff914d',
        yellowProject: '#ffdd4d',
        pinkSecondaryProject: '#ff004d',
        purpleComercialProject: '#5E17EB',

        // ── Tokens del rediseño (prefijo ds) ────────────────────────────────
        // El prototipo NO tiene modo oscuro. Estos valores están derivados
        // conservando el matiz e invirtiendo la luminosidad, para que el módulo
        // no se vea roto con el tema oscuro activo. Sin validación de diseño:
        // revisar antes de dar el rediseño por terminado.
        dsInk: '#d8d0e7',
        dsText: '#b4aec0',
        dsTextMuted: '#8a849d',
        dsTextSubtle: '#6f687f',
        dsTextFaint: '#575263', // = invert(0.92) hue-rotate(180) de #b6b0c4 (era #453e51, muy oscuro)
        dsLineStrong: '#453e51', // = invert(0.92) hue-rotate(180) de #cfc7de (era #363140, muy oscuro)
        dsLine: '#363042',
        dsBorder: '#2c2933',
        dsDivider: '#25222a',
        dsBg: '#201e23',
        dsRowUnread: '#242129',
        dsRowHover: '#1c1b1e',
        dsPurple700: '#e5b7fb',
        dsPurple50: '#242129',
        dsTeal400: '#008877',
        dsTeal600: '#239b8e',
        dsTeal700: '#4caca2',
        dsTeal800: '#4caca2',
        dsTeal50: '#142421',
        dsDanger: '#ff89a8',
        dsDangerDot: '#ff89a8',
        dsDangerBg: '#271b1d',
        dsDangerOnDark: '#ff9db4',
        dsWarn: '#bf883a',
        dsWarnDot: '#9e5300',
        dsWarnBg: '#251c0b',

        // Cimiento del portal del agente, variante oscura derivada (el
        // prototipo no tiene modo oscuro; sin validación de diseño).
        dsSurfaceSubtle: '#19181a',
        dsPurple100: '#242129',
        dsWarnBorder: '#412800',
        dsDangerBgSoft: '#271b1d',
        dsLinePurple: '#363042',
        dsLinePurpleSoft: '#3a3350',
        dsPurple500: '#9985ed',
        dsSurfaceHover: '#1c1b1e',

        // Task 1.28 (#8554), variante oscura derivada (el prototipo no tiene oscuro).
        dsWarnBgSoft: '#251c0b',
        dsOkBgSoft: '#142421',
        dsOkBorder: '#1f5049',
        dsViewerBar: '#1e3a8a',
        // Versión final, variante oscura derivada (el prototipo no tiene oscuro).
        dsPurple08: 'rgba(229, 183, 251, 0.16)',
        // Control deshabilitado, variante oscura derivada.
        dsDisabledBg: '#212629',
        dsDisabledText: '#5b636b',
        // Superficie de tarjeta ds en oscuro: near-black del invert del rediseño.
        dsSurface: '#141414',

        // Estados §8, variante oscura derivada (el prototipo no tiene modo
        // oscuro; sin validación de diseño, igual que el resto de dark de §8).
        dsWarnBlockBg: '#2a2010',
        dsTealBlockBg: '#182b28',
        dsTealBlockBorder: '#1f3d38',
        dsDangerBlockBorder: '#4a2530'
    },
    loginColors: {
        left: '#755dd9',
        right: '#511A6B'
    }
};

/**
 * Configuración por módulo. Cada marca declara qué partes enciende.
 * PyLC enciende todo porque el prototipo se diseñó sobre sus procesos
 * (Convención, capacitaciones, campañas). Otra marca que no declare nada
 * recibe solo los paneles base. Ver `rediseno-tablero/CORE.md`.
 */
export const modulesConfig = {
    miTablero: {
        paneles: ['kpis', 'folios', 'agenda', 'comunicados', 'destacados', 'convencion', 'tiempoRespuesta']
    }
};

export const config = {
    company: companyConfig,
    assets: assetsConfig,
    colors: colorsConfig,
    typography: typographyConfig,
    modules: modulesConfig
};

// Exportación por defecto
export default config;
